<script setup lang="ts">
import Modal from '@/components/modal/Modal.vue';
import AppDatePicker from '@/corePagesModule/components/AppDatePicker.vue';
import { FieldItem, ValidateRule } from '@/coreDomainsModule/constant/validateRules';
import NumberInputWithComma from '@/corePagesModule/components/NumberInputWithComma.vue';
import { useForms } from '@/corePagesModule/composables/useForm';
import { i18n } from '@/i18n';
import Button from '@/components/input/Btn/Btn.vue';
import { computed, ref, watch } from 'vue';
import { loadMyPropertyPreferences, myPropertyPreferences, saveMyPropertyPreferences } from '@/appLogic/view/preferences';
import { IDate } from '@/coreDomainsModule/date';
import { DateService } from '@/coreDomainsModule/services/dateService';
import Mdi from '@/corePagesModule/components/Mdi.vue';
import { CombinedOrderSettingModalStatus, ICombinedOrderConditions } from '@/InventoryControlModule/domains/entities/CombinedOrder';
import RowCheckBox from '@/components/atom/RowCheckBox.vue';

const t = i18n.t;

const INPUT_FIELD_LIST = [
  { priority: 1, prefix: 'primary' },
  { priority: 2, prefix: 'secondary' },
] as const;

const props = withDefaults(
  defineProps<{
    modalState: CombinedOrderSettingModalStatus;
    defaultDesiredDeliveryDate?: IDate | null;
  }>(),
  { defaultDesiredDeliveryDate: null }
);

const emits = defineEmits<{ (e: 'close'); (e: 'confirm', combineOrderSetting: ICombinedOrderConditions) }>();

const fieldItems: FieldItem[] = [
  {
    name: 'primaryTotalOrderAmount',
    rules: [ValidateRule.Required, ValidateRule.HalfWidthRealNumber, ValidateRule.NegativeNumber],
  },
  {
    name: 'primaryTurnOverUpperLimit',
    rules: [ValidateRule.Required, ValidateRule.HalfWidthRealNumber],
  },
  {
    name: 'secondaryTotalOrderAmount',
    rules: [ValidateRule.Required, ValidateRule.HalfWidthRealNumber, ValidateRule.NegativeNumber],
  },
  {
    name: 'secondaryTurnOverUpperLimit',
    rules: [ValidateRule.Required, ValidateRule.HalfWidthRealNumber],
  },
];

const { fieldState, allValidate, resetForm } = useForms(fieldItems);

const desiredDeliveryDate = ref<{ primary: IDate | null; secondary: IDate | null }>({ primary: null, secondary: null });

const isMOQConsidered = ref<{ primary: boolean; secondary: boolean }>({ primary: true, secondary: true });

const modalState = computed(() => props.modalState !== 'close');

const isDefaultSetting = computed(() => props.modalState === 'setting');

const isErrorDesiredDeliveryDate = computed(() => ({
  primary:
    desiredDeliveryDate.value.primary === null ||
    DateService.compareDates(desiredDeliveryDate.value.primary, DateService.convertDateToIDate(new Date())) === -1,
  secondary:
    desiredDeliveryDate.value.secondary === null ||
    DateService.compareDates(desiredDeliveryDate.value.secondary, DateService.convertDateToIDate(new Date())) === -1,
}));

const isErrorWithPrefix = computed(() => ({
  primary:
    fieldState['primaryTotalOrderAmount'].isError.value ||
    fieldState['primaryTurnOverUpperLimit'].isError.value ||
    (props.modalState === 'generate' && isErrorDesiredDeliveryDate.value.primary),
  secondary:
    fieldState['secondaryTotalOrderAmount'].isError.value ||
    fieldState['secondaryTurnOverUpperLimit'].isError.value ||
    (props.modalState === 'generate' && isErrorDesiredDeliveryDate.value.secondary),
}));

const inputFieldGridTemplate = computed(() => (isDefaultSetting.value ? '1fr 1fr' : '1fr 1fr 1fr'));

const modalTitle = computed(() => t(isDefaultSetting.value ? 'まとめ発注デフォルト設定' : 'まとめ発注案の生成'));

const confirmButtonText = computed(() => (props.modalState === 'progress' ? 'まとめ発注の案を生成中...' : 'まとめ発注の案を生成'));

/**
 * desiredDeliveryDateの値をリセットする
 */
const resetdesiredDeliveryDateValue = () => {
  desiredDeliveryDate.value = { primary: null, secondary: null };
};

/**
 * inputの値が変更された際のハンドラ
 * @param inputNamePrefix - 変更したinputが 'primary' | 'secondary' のどちらか
 * @param inputType - 変更したinputの種類
 * @param value - 変更された値
 */
const onInputHandler = (inputNamePrefix: 'primary' | 'secondary', inputType: 'TotalOrderAmount' | 'TurnOverUpperLimit', value: number) => {
  // それぞれ合計発注数量は整数に、上限在庫回転率は第1桁で切り捨てたものを文字列にしてfieldStateに保存
  fieldState[inputNamePrefix + inputType].setValue(
    inputType === 'TotalOrderAmount' ? Math.floor(value).toString() : (Math.floor(value * 10) / 10).toString()
  );
};

/**
 * desiredDeliveryDateの値が変更された際のハンドラ
 * @param inputNamePrefix - 変更したinputが 'primary' | 'secondary' のどちらか
 * @param value - 変更された値
 */
const onInputDesiredDeliveryDateHandler = (inputNamePrefix: 'primary' | 'secondary', value: Date) => {
  if (desiredDeliveryDate.value.primary === null && desiredDeliveryDate.value.secondary === null) {
    desiredDeliveryDate.value.primary = DateService.convertDateToIDate(value);
    desiredDeliveryDate.value.secondary = DateService.convertDateToIDate(value);
  } else {
    desiredDeliveryDate.value[inputNamePrefix] = DateService.convertDateToIDate(value);
  }
};

/**
 * 戻るボタンクリック時の処理
 */
const handleBack = () => {
  if (props.modalState) {
    resetForm(fieldItems);
    resetdesiredDeliveryDateValue();
    emits('close');
  }
};

/**
 * 設定ボタンクリック時の処理
 */
const handleNextSetting = async () => {
  if (!allValidate()) {
    return;
  }
  await saveCondition();
  emits('close');
};

/**
 * まとめ発注生成ボタンクリック時の処理
 */
const handleNextConfirm = async () => {
  if (!allValidate() || !desiredDeliveryDate.value.primary || !desiredDeliveryDate.value.secondary) {
    return;
  }
  emits('confirm', {
    primary: {
      totalOrderAmount: Number(fieldState['primaryTotalOrderAmount'].value.value),
      turnOverUpperLimit: Number(fieldState['primaryTurnOverUpperLimit'].value.value),
      desiredDeliveryDate: desiredDeliveryDate.value.primary,
      isMOQConsidered: isMOQConsidered.value.primary,
    },
    secondary: {
      totalOrderAmount: Number(fieldState['secondaryTotalOrderAmount'].value.value),
      turnOverUpperLimit: Number(fieldState['secondaryTurnOverUpperLimit'].value.value),
      desiredDeliveryDate: desiredDeliveryDate.value.secondary,
      isMOQConsidered: isMOQConsidered.value.secondary,
    },
  });
};

/**
 * まとめ発注デフォルト設定をIndexedDBに保存する
 */
const saveCondition = async () => {
  myPropertyPreferences.order.combineOrderDefaultCond = {
    primary: {
      totalOrderAmount: Number(fieldState['primaryTotalOrderAmount'].value.value),
      turnOverUpperLimit: Number(fieldState['primaryTurnOverUpperLimit'].value.value),
      isMOQConsidered: isMOQConsidered.value.primary,
    },
    secondary: {
      totalOrderAmount: Number(fieldState['secondaryTotalOrderAmount'].value.value),
      turnOverUpperLimit: Number(fieldState['secondaryTurnOverUpperLimit'].value.value),
      isMOQConsidered: isMOQConsidered.value.secondary,
    },
  };
  await saveMyPropertyPreferences();
};

watch(
  () => props.modalState,
  async (value) => {
    if (value === 'close' || value === 'progress') return;
    await loadMyPropertyPreferences();
    const combineOrderDefaultCondData = myPropertyPreferences.order.combineOrderDefaultCond;
    if (!combineOrderDefaultCondData || !fieldState) return;
    for (const inputField of INPUT_FIELD_LIST) {
      fieldState[inputField.prefix + 'TotalOrderAmount'].value.value =
        combineOrderDefaultCondData[inputField.prefix].totalOrderAmount.toString();
      fieldState[inputField.prefix + 'TurnOverUpperLimit'].value.value =
        combineOrderDefaultCondData[inputField.prefix].turnOverUpperLimit.toString();
      isMOQConsidered.value[inputField.prefix] = combineOrderDefaultCondData[inputField.prefix].isMOQConsidered;
    }
    if (value === 'generate' && props.defaultDesiredDeliveryDate !== null) {
      desiredDeliveryDate.value.primary = props.defaultDesiredDeliveryDate;
      desiredDeliveryDate.value.secondary = props.defaultDesiredDeliveryDate;
    } else {
      resetdesiredDeliveryDateValue();
    }
  },
  { immediate: true }
);
</script>
<template>
  <Modal
    :modal-state="modalState"
    padding="0"
    border-radius="16px"
    :no-padding-and-margin="false"
    modal-size="max-content"
    :is-force-no-style="true"
    :modal-width="580"
    md-click-out-side-to-close
    scroll-off
    no-cancel
    is-modal-overflow-hidden
    @outside-click="handleBack"
  >
    <div class="modal">
      <div class="header">
        <h1 class="header__title">{{ modalTitle }}</h1>
        <p class="header__description">{{ t('優先順位1で計算後、平均回転率が上限を超える場合は優先順位2を使用') }}</p>
      </div>
      <template v-for="inputField in INPUT_FIELD_LIST" :key="inputField.priority">
        <div class="input-field" :class="'input-field--' + inputField.prefix">
          <div class="input-field__title__container">
            <div class="input-field__title">{{ t('優先順位') + inputField.priority }}</div>
            <div v-show="isErrorWithPrefix[inputField.prefix]" class="input-field__title__error">
              {{ t('正しい値を入力してください') }}
            </div>
          </div>
          <div class="input-field-content">
            <div v-if="!isDefaultSetting" class="input-field-content__container">
              <div class="input-field-content__label">{{ t('希望納期') + '*' }}</div>
              <AppDatePicker
                :model-value="
                  desiredDeliveryDate[inputField.prefix] &&
                  DateService.convertIDateToDate(desiredDeliveryDate[inputField.prefix] ?? { year: 9999, month: 99, day: 99 })
                "
                class="calendar"
                :class="{ 'error-border': isErrorDesiredDeliveryDate[inputField.prefix] }"
                :mode-height="1"
                :enable-time-picker="false"
                :month-change-on-scroll="false"
                :teleport="true"
                format="yyyy/MM/dd"
                week-start="0"
                min-date="today"
                :locale="i18n.locale"
                :select-text="t('選択')"
                :cancel-text="t('キャンセル')"
                :show-now-button="true"
                :clearable="false"
                @update:model-value="onInputDesiredDeliveryDateHandler(inputField.prefix, $event)"
              >
                <template #dp-input="{ isMenuOpen }">
                  <div v-if="!desiredDeliveryDate[inputField.prefix]" class="calendar__icon__container">
                    <Mdi class="calendar__icon" icon="calendar_today" />
                  </div>
                  <div v-else class="calendar__text__container">
                    <div class="calendar__text">
                      {{
                        DateService.convertIYearMonthDayToYYYYMMDDWithSlash(
                          desiredDeliveryDate[inputField.prefix] ?? { year: 9999, month: 99, day: 99 },
                          true
                        )
                      }}
                    </div>
                    <Mdi class="calendar__icon" :icon="isMenuOpen ? 'expand_less' : 'expand_more'" />
                  </div>
                </template>
              </AppDatePicker>
            </div>
            <div class="input-field-content__container">
              <div class="input-field-content__label">{{ t('合計発注数量') + '*' }}</div>
              <NumberInputWithComma
                :model-value="Number(fieldState[inputField.prefix + 'TotalOrderAmount'].value.value)"
                class="input-field-content__input"
                :class="{ 'error-border': fieldState[inputField.prefix + 'TotalOrderAmount'].isError.value }"
                @input="(e) => onInputHandler(inputField.prefix, 'TotalOrderAmount', e)"
              />
            </div>
            <div class="input-field-content__container">
              <div class="input-field-content__label">{{ t('上限在庫回転率') + '*' }}</div>
              <NumberInputWithComma
                :model-value="Number(fieldState[inputField.prefix + 'TurnOverUpperLimit'].value.value)"
                class="input-field-content__input"
                :class="{ 'error-border': fieldState[inputField.prefix + 'TurnOverUpperLimit'].isError.value }"
                @input="(e) => onInputHandler(inputField.prefix, 'TurnOverUpperLimit', e)"
              />
            </div>
            <div class="input-field-content--checkbox">
              <RowCheckBox
                :is-checked="isMOQConsidered[inputField.prefix]"
                :label-text="t('必ずMOQを満たすように発注案を生成する')"
                class="moq-checkbox"
                @change-status="(value) => (isMOQConsidered[inputField.prefix] = value)"
              />
            </div>
          </div>
        </div>
      </template>
      <div class="content-desc">
        <div class="content-desc__title">{{ t('配分ロジック') }}</div>
        <p class="content-desc__text">
          {{ t('1. 優先順位1で在庫回転率が平均的になるように配分を計算') + '\n' }}
          {{ t('2. 平均回転率が上限を超える場合は優先順位2で再計算') + '\n' }}
          {{ t('3. 在庫回転率が低い商品に優先的に配分') + '\n' }}
        </p>
      </div>
      <div class="footer">
        <div v-if="isDefaultSetting" class="button-container">
          <Button class="button button--cancel" @click="handleBack">
            {{ t('戻る') }}
          </Button>
          <Button class="button button--save" @click="handleNextSetting">
            {{ t('設定') }}
          </Button>
        </div>
        <div v-else class="button-container">
          <Button class="button button--save" @click="handleNextConfirm">
            {{ t(confirmButtonText) }}
          </Button>
        </div>
      </div>
    </div>
  </Modal>
</template>
<style lang="scss" scoped>
.modal {
  display: grid;
  gap: 24px;
  padding: 24px 16px;
  background-color: $semanticWhite;

  & * {
    font-family: Noto Sans JP;
  }
}

.header {
  display: grid;
  gap: 8px;
  &__title {
    font-size: 22px;
    font-weight: 700;
    line-height: 1.3;
    text-align: center;
    color: $textDefault;
  }

  &__description {
    font-size: 16px;
    font-weight: 700;
    line-height: 1;
    text-align: center;
    color: $textSecondary;
  }
}

.input-field {
  border-radius: 8px;
  padding: 16px;
  display: grid;
  gap: 16px;
  &--primary {
    border: solid 2px $textDefault;
  }
  &--secondary {
    border: solid 1px $borderDefault;
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
    line-height: 1;
    color: $textDefault;

    &__container {
      display: grid;
      grid-template-columns: max-content max-content;
      gap: 8px;
      align-items: center;
    }

    &__error {
      font-size: 14px;
      font-weight: 400;
      line-height: 1;
      color: $semanticError;
    }
  }
  &-content {
    display: grid;
    grid-template-columns: v-bind(inputFieldGridTemplate);
    column-gap: 16px;
    row-gap: 8px;
    &--checkbox {
      grid-column: 1 / -1;
      display: grid;
      grid-template-columns: max-content auto;
      align-items: center;
      gap: 8px;
    }
    &__container {
      display: grid;
    }
    &__label {
      padding: 8px 0;
      font-size: 14px;
      font-weight: 400;
      line-height: 1.3;
      color: $textDefault;
    }
    &__input {
      height: 32px;
      padding: 0 8px;
      border: 1px solid $borderDefault;
      border-radius: 4px;
      font-size: 14px;
      font-weight: 400;
      line-height: 1;
      color: $textDefault;
    }
  }
}

.calendar {
  display: grid;
  height: 32px;
  padding: 0 8px;
  border-radius: 4px;
  justify-content: center;
  align-items: center;
  border: 1px solid $borderDefault;
  cursor: pointer;
  grid-template-columns: 1fr;

  &__icon {
    height: 100%;
    font-family: 'Material Icons';
    font-size: 18px;
    color: $textSecondary;
    &__container {
      display: grid;
      width: 100%;
      justify-content: center;
      align-items: center;
    }
  }

  &__text {
    font-size: 14px;
    line-height: 1;
    color: $textDefault;
    &__container {
      display: grid;
      width: 100%;
      justify-content: space-between;
      align-items: center;
      grid-template-columns: auto auto;
    }
  }
}

.content-desc {
  display: grid;
  gap: 8px;
  padding: 16px;
  border-radius: 8px;
  background-color: $surfaceDefault;

  &__title {
    font-size: 16px;
    font-weight: 700;
    line-height: 1;
    color: $textSecondary;
  }
  &__text {
    font-size: 14px;
    font-weight: 400;
    line-height: 1.3;
    color: $textSecondary;
    white-space: pre-line;
  }
}

.footer {
  display: grid;
  gap: 16px;
  justify-items: center;
}

.button {
  //min-width: 160px;
  height: 40px;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0em;
  text-align: center;
  border-radius: 4px;
  padding: 10px 24px;
  box-shadow: none;
  margin: 0;

  &--cancel {
    background-color: $semanticWhite;
    color: $semanticPrimary;
    border: 1px solid $semanticPrimary;
  }
  &--save {
    color: $semanticWhite;
    background-color: $semanticPrimary;
    border: 1px solid $semanticPrimary;
  }

  &-container {
    display: grid;
    gap: 10px;
    grid-auto-flow: column;
    justify-content: center;
  }
}
.error-border {
  border: 2px solid $semanticError;
}
.moq-checkbox {
  display: grid;
  grid-template-columns: auto auto;
  align-items: center;
  gap: 8px;
  line-height: 0;
}
</style>
